/**
 * Buffers a published app's items stream ahead of the view that renders it.
 *
 * `/apps/:id` opens `…/items/data` on mount and paints widgets as frames land, so its first paint
 * waits on the server running every widget query. Favouriting an app declares it one the viewer
 * opens routinely, which is what makes running that stream at sign-in worthwhile: the frames are
 * buffered here, and the view replays them from memory instead of asking for them again.
 *
 * Replay is deliberately frame-by-frame rather than a finished result. A prefetch the viewer beats
 * to the app is still useful — they attach to the running stream and see widgets appear as they
 * complete, which is exactly what they would have seen had they opened it themselves.
 *
 * This is a prefetch window, not a cache: a buffered stream stops being replayable
 * `PUBLISHED_APP_PREFETCH_TTL_MS` after it finishes, and a failed one is dropped so the view falls
 * back to a live request rather than inheriting the failure.
 */

import { PUBLISHED_APP_PREFETCH_TTL_MS } from "@/constants";
import {
  parsePublishedAppItemsSseChunk,
  type PublishedAppItemsStreamEvent,
} from "@/features/published-apps/lib/publishedAppItemsStream";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

/** Terminal states mirror the stream hook's, so a replayed stream ends the way a live one would. */
export type PrefetchedStreamOutcome = "streaming" | "complete" | "error" | "unavailable";

export type PrefetchedStreamSubscriber = {
  onEvent: (event: PublishedAppItemsStreamEvent) => void;
  onSettled: (outcome: Exclude<PrefetchedStreamOutcome, "streaming">, error: string | null) => void;
};

export type PrefetchedStreamHandle = {
  /** `streaming` when frames are still arriving and `onSettled` is yet to fire. */
  outcome: PrefetchedStreamOutcome;
  error: string | null;
  unsubscribe: () => void;
};

type PrefetchedStreamEntry = {
  events: PublishedAppItemsStreamEvent[];
  outcome: PrefetchedStreamOutcome;
  error: string | null;
  /** When the stream finished; null while it is still running, which never expires. */
  settledAt: number | null;
  subscribers: Set<PrefetchedStreamSubscriber>;
  abortController: AbortController;
};

type StreamFetcher = (path: string, init: RequestInit) => Promise<Response>;

const bufferedStreams = new Map<string, PrefetchedStreamEntry>();

function isReplayable(entry: PrefetchedStreamEntry): boolean {
  if (entry.outcome === "error" || entry.outcome === "unavailable") {
    // Handing a subscriber a failure the prefetch hit in the background would show an error for a
    // request the viewer never made. Let them issue their own instead.
    return false;
  }
  if (entry.settledAt === null) {
    return true;
  }
  return Date.now() - entry.settledAt < PUBLISHED_APP_PREFETCH_TTL_MS;
}

function settle(
  entry: PrefetchedStreamEntry,
  outcome: Exclude<PrefetchedStreamOutcome, "streaming">,
  error: string | null,
) {
  entry.outcome = outcome;
  entry.error = error;
  entry.settledAt = Date.now();
  for (const subscriber of [...entry.subscribers]) {
    subscriber.onSettled(outcome, error);
  }
  entry.subscribers.clear();
}

/**
 * Open `path` and buffer its frames under `key` — the same key the view's stream source uses, which
 * is what lets the view find this buffer. Resolves when the stream ends; callers that only want the
 * warm buffer can ignore the promise. A key that is already buffered and still replayable is left
 * alone, so calling this repeatedly costs nothing.
 */
export async function prefetchAppItemsStream(
  key: string,
  path: string,
  fetcher: StreamFetcher,
): Promise<void> {
  const existing = bufferedStreams.get(key);
  if (existing && isReplayable(existing)) {
    return;
  }

  const entry: PrefetchedStreamEntry = {
    events: [],
    outcome: "streaming",
    error: null,
    settledAt: null,
    subscribers: new Set(),
    abortController: new AbortController(),
  };
  bufferedStreams.set(key, entry);

  try {
    const response = await fetcher(path, {
      method: "GET",
      headers: { Accept: "text/event-stream" },
      signal: entry.abortController.signal,
    });

    if (response.status === 404) {
      settle(entry, "unavailable", null);
      return;
    }
    if (!response.ok) {
      settle(entry, "error", await readApiErrorMessage(response));
      return;
    }
    if (!response.body) {
      settle(entry, "error", "Stream response did not include a body.");
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const parsed = parsePublishedAppItemsSseChunk(buffer);
      buffer = parsed.remainder;

      for (const event of parsed.events) {
        entry.events.push(event);
        for (const subscriber of [...entry.subscribers]) {
          subscriber.onEvent(event);
        }
      }
    }

    settle(entry, "complete", null);
  } catch (streamError) {
    settle(
      entry,
      "error",
      streamError instanceof Error ? streamError.message : "Unable to load charts and tables.",
    );
  }
}

/**
 * Replay the buffered stream for `key`, or return null when there is nothing usable to replay — the
 * signal to the caller that it must open the stream itself.
 *
 * Frames buffered so far are delivered synchronously before this returns; if the prefetch is still
 * running, the subscriber then receives the rest as they arrive.
 */
export function subscribeToPrefetchedAppItemsStream(
  key: string,
  subscriber: PrefetchedStreamSubscriber,
): PrefetchedStreamHandle | null {
  const entry = bufferedStreams.get(key);
  if (!entry) {
    return null;
  }
  if (!isReplayable(entry)) {
    bufferedStreams.delete(key);
    return null;
  }

  for (const event of entry.events) {
    subscriber.onEvent(event);
  }

  if (entry.outcome === "streaming") {
    entry.subscribers.add(subscriber);
    return {
      outcome: "streaming",
      error: null,
      unsubscribe: () => {
        entry.subscribers.delete(subscriber);
      },
    };
  }

  return {
    outcome: entry.outcome,
    error: entry.error,
    unsubscribe: () => {},
  };
}

/** True when `key` has a buffer a view would replay. Exposed for the prefetch runner and tests. */
export function hasPrefetchedAppItemsStream(key: string): boolean {
  const entry = bufferedStreams.get(key);
  return entry !== undefined && isReplayable(entry);
}

/** Drop every buffer and abort anything still in flight. Called when the session ends. */
export function clearPrefetchedAppItemsStreams(): void {
  for (const entry of bufferedStreams.values()) {
    entry.abortController.abort();
    entry.subscribers.clear();
  }
  bufferedStreams.clear();
}
